import { matchPath } from 'react-router';

import { call, put, select, takeLatest, take, takeEvery, debounce, fork } from 'redux-saga/effects';

import * as navigateActions from 'actions/navigateActions';
import { restClient } from 'config/store/configureStore';
import * as GlobalActionTypes from 'constants/ActionTypes';
import { NEW_ENTITY_ID } from 'constants/EntityTitles';
import { PATH } from 'constants/RoutesConstants';

import { APPLICATION_IS_READY } from 'modules/app/actionTypes';
import { applicationIsReady } from 'modules/app/selectors';
import { getSelectedId } from 'modules/predefinedMessages/selectors/details';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import { createNotification } from 'modules/toast-notifications/actions';

import { getDealerDefaultLanguage } from 'selectors/dealerSelectors';
import { getCurrentLocation } from 'selectors/router';

import * as actions from '../actions';
import * as actionTypes from '../actionTypes';
import bootloader from './bootloader.sagas';
import { getParams as getDealerParams } from '../selectors/dashboard/dealer';

function* refreshDashboard() {
  const dealerParams = yield select(getDealerParams);
  yield put(actions.fetchAllByDealerId(dealerParams));
}

function* startDashboardApplication() {
  const location = yield select(getCurrentLocation);
  const isReady = yield select(applicationIsReady);
  const isRightLocation = matchPath(PATH.PREDEFINED_MESSAGES_DASHBOARD.path, location);

  if (isRightLocation) {
    if (!isReady) {
      yield take(GlobalActionTypes.FETCH_DEALER_SUCCESS);
    }

    yield put(actions.refreshDashboard());
  }
}

function* startDetailsApplication({ payload }) {
  const match = matchPath(PATH.PREDEFINED_MESSAGES_DETAILED.path, payload.location.pathname);

  if (match) {
    const isReady = yield select(applicationIsReady);
    const { predefinedMessageId } = match.params;
    if (!isReady) {
      yield take(APPLICATION_IS_READY);
    }

    const defaultLanguage = yield select(getDealerDefaultLanguage);
    let languages = [defaultLanguage];

    const selectedId = yield select(getSelectedId);
    const isNotAlreadySelected = selectedId !== predefinedMessageId;

    if (isNotAlreadySelected) {
      try {
        if (predefinedMessageId !== NEW_ENTITY_ID) {
          yield put(actions.fetchPredefinedMessage(predefinedMessageId));
          const response = yield take([
            actionTypes.FETCH_PREDEFINED_MESSAGE_SUCCESS,
            actionTypes.FETCH_PREDEFINED_MESSAGE_FAILURE,
          ]);
          if (response.type === actionTypes.FETCH_PREDEFINED_MESSAGE_FAILURE) {
            throw response.payload;
          }

          const { languages: predefinedMessageLanguages = [] } = response.payload;
          languages = [
            defaultLanguage,
            ...predefinedMessageLanguages.filter(lang => lang !== defaultLanguage),
          ];
        }
        yield put(actions.selectPredefinedMessage(predefinedMessageId));
        yield put(actions.setLanguages(languages));
        yield put(actions.selectLanguage(defaultLanguage));
        yield put(actions.applicationReadySuccess());
      } catch (error) {
        yield put(actions.applicationReadyFailure(error));
      }
    }
  }
}

function* fetchPredefinedMessages({ type, payload }) {
  const { request, ...params } = payload;
  try {
    const { data: response } = yield call(restClient.request, request);
    yield put(actions.genericFetchPredefinedMessagesSuccess(type)({ ...response, params }));
  } catch (error) {
    yield put(actions.genericFetchPredefinedMessagesFailure(type)(error));
  }
}

function* fetchPredefinedMessage({ payload }) {
  const { request } = payload;
  try {
    const { data: response } = yield call(restClient.request, request);
    yield put(actions.fetchPredefinedMessageSuccess(response));
  } catch (error) {
    yield put(actions.fetchPredefinedMessageFailure(error));
  }
}

function* fetchFindByDealerIdOrGroup({ payload }) {
  const { request, ...params } = payload;

  try {
    const { data: response } = yield call(restClient.request, request);
    yield put(actions.fetchFindByDealerOrGroupSuccess(response, params));
  } catch (error) {
    yield put(actions.fetchFindByDealerOrGroupFailure(error));
  }
}

function* create({ payload }) {
  const { request } = payload;
  try {
    const { data: response } = yield call(restClient.request, request);
    yield put(actions.createSuccess(response));
    yield call(navigateActions.navigateToPredefinedMessagesDetails, response.id);
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.fiche.notification.create_predefined_message.success',
          defaultMessage: 'Predefined message created',
        },
      })
    );
  } catch (error) {
    yield put(actions.createFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.fiche.notification.create_predefined_message.failure',
          defaultMessage: "Can't create the predefined message",
        },
      })
    );
  }
}

function* update({ payload }) {
  const { request } = payload;
  try {
    const { data: response } = yield call(restClient.request, request);
    yield put(actions.updateSuccess(response));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.fiche.notification.update_predefined_message.success',
          defaultMessage: 'Predefined message updated',
        },
      })
    );
  } catch (error) {
    yield put(actions.updateFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.fiche.notification.update_predefined_message.failure',
          defaultMessage: "Can't update the predefined message",
        },
      })
    );
  }
}

function* deletePredefinedMessage({ payload }) {
  const { id, request } = payload;
  try {
    yield call(restClient.request, request);
    yield put(actions.deletePredefinedMessageSuccess(id));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.fiche.notification.delete_predefined_message.success',
          defaultMessage: 'Predefined message deleted',
        },
      })
    );
  } catch (error) {
    yield put(actions.deletePredefinedMessageFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.fiche.notification.delete_predefined_message.failure',
          defaultMessage: "Can't delete the predefined message",
        },
      })
    );
  }
}

function* clone({ payload }) {
  const { request } = payload;

  try {
    const { data: predefinedMessage } = yield call(restClient.request, request);
    yield put(actions.clonePredefinedMessageSuccess(predefinedMessage));

    const origin = yield select(getCurrentLocation);
    yield call(navigateActions.navigateToPredefinedMessagesDetails, predefinedMessage.id, { origin });
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.fiche.notification.clone_predefined_message.success',
          defaultMessage: 'Predefined message cloned',
        },
      })
    );
  } catch (error) {
    yield put(actions.clonePredefinedMessageFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.fiche.notification.clone_predefined_message.failure',
          defaultMessage: "Can't clone the predefined message",
        },
      })
    );
  }
}

export default function* predefinedMessagesSagas() {
  yield fork(bootloader);
  const fetchAllTypes = [actionTypes.FETCH_PREDEFINED_MESSAGES, actionTypes.FETCH_ALL_BY_DEALER]; // TODO - ADD GROUP
  yield takeEvery(fetchAllTypes, fetchPredefinedMessages);
  yield takeLatest(LOCATION_CHANGE, startDetailsApplication);
  yield takeLatest([LOCATION_CHANGE, GlobalActionTypes.SELECT_DEALER], startDashboardApplication);
  yield takeLatest(actionTypes.REFRESH_DASHBOARD, refreshDashboard);
  yield debounce(500, actionTypes.SET_DASHBOARD_FILTERS, refreshDashboard);
  yield takeLatest(actionTypes.FETCH_PREDEFINED_MESSAGE, fetchPredefinedMessage);
  yield takeLatest(actionTypes.CREATE, create);
  yield takeLatest(actionTypes.UPDATE, update);
  yield takeLatest(actionTypes.DELETE, deletePredefinedMessage);
  yield takeLatest(actionTypes.CLONE, clone);
  yield takeLatest(actionTypes.FETCH_FIND_DEALER_OR_GROUP, fetchFindByDealerIdOrGroup);
}
